/**
 * HTML Syntax Highlighter
 * Provides syntax highlighting for HTML code in the code editor
 */

import { isValidColor, parseColor, toRgbaString } from './color-detector';

export interface HighlightToken {
  type: 'tag' | 'attribute' | 'attribute-class' | 'attribute-id' | 'class-name' | 'id-value' | 'string' | 'comment' | 'text' | 'doctype' | 'color';
  value: string;
  colorPreview?: string; // For color values
}

/**
 * Tokenize HTML code into syntax tokens
 */
export function tokenizeHtml(html: string): HighlightToken[] {
  const tokens: HighlightToken[] = [];
  let i = 0;

  while (i < html.length) {
    // Check for HTML comments
    if (html.substring(i, i + 4) === '<!--') {
      const endIndex = html.indexOf('-->', i + 4);
      if (endIndex !== -1) {
        tokens.push({
          type: 'comment',
          value: html.substring(i, endIndex + 3),
        });
        i = endIndex + 3;
        continue;
      }
    }

    // Check for DOCTYPE declaration
    if (html.substring(i, i + 2).toLowerCase() === '<!' && html.substring(i, i + 9).toLowerCase() === '<!doctype') {
      const endIndex = html.indexOf('>', i);
      if (endIndex !== -1) {
        tokens.push({
          type: 'doctype',
          value: html.substring(i, endIndex + 1),
        });
        i = endIndex + 1;
        continue;
      }
    }

    // Check for HTML tags
    if (html[i] === '<') {
      const endIndex = html.indexOf('>', i);
      if (endIndex !== -1) {
        const tagContent = html.substring(i, endIndex + 1);
        parseTag(tagContent, tokens);
        i = endIndex + 1;
        continue;
      }
    }

    // Everything else is text
    const nextTag = html.indexOf('<', i);
    const textContent = nextTag === -1 ? html.substring(i) : html.substring(i, nextTag);

    if (textContent) {
      tokens.push({
        type: 'text',
        value: textContent,
      });
    }

    i = nextTag === -1 ? html.length : nextTag;
  }

  return tokens;
}

/**
 * Parse an HTML tag into tokens
 */
function parseTag(tagString: string, tokens: HighlightToken[]) {
  // Opening bracket and tag name
  const tagNameMatch = tagString.match(/^<\/?([a-zA-Z][a-zA-Z0-9]*)/);

  if (!tagNameMatch) {
    // Invalid tag, treat as text
    tokens.push({ type: 'text', value: tagString });
    return;
  }

  const tagStart = tagString.substring(0, tagNameMatch[0].length);
  tokens.push({ type: 'tag', value: tagStart });

  let i = tagStart.length;

  // Parse attributes
  while (i < tagString.length - 1) { // -1 to exclude the closing >
    // Skip whitespace
    while (i < tagString.length && /\s/.test(tagString[i])) {
      tokens.push({ type: 'text', value: tagString[i] });
      i++;
    }

    if (i >= tagString.length - 1) break;

    // Check for self-closing or closing bracket
    if (tagString[i] === '/' || tagString[i] === '>') {
      break;
    }

    // Parse attribute name
    const attrNameMatch = tagString.substring(i).match(/^([a-zA-Z][a-zA-Z0-9-]*)/);

    if (attrNameMatch) {
      const attrName = attrNameMatch[0].toLowerCase();

      // Special handling for class and id attributes
      if (attrName === 'class') {
        tokens.push({ type: 'attribute-class', value: attrNameMatch[0] });
      } else if (attrName === 'id') {
        tokens.push({ type: 'attribute-id', value: attrNameMatch[0] });
      } else {
        tokens.push({ type: 'attribute', value: attrNameMatch[0] });
      }

      i += attrNameMatch[0].length;

      // Skip whitespace
      while (i < tagString.length && /\s/.test(tagString[i])) {
        tokens.push({ type: 'text', value: tagString[i] });
        i++;
      }

      // Check for = sign
      if (i < tagString.length && tagString[i] === '=') {
        tokens.push({ type: 'text', value: '=' });
        i++;

        // Skip whitespace
        while (i < tagString.length && /\s/.test(tagString[i])) {
          tokens.push({ type: 'text', value: tagString[i] });
          i++;
        }

        // Parse attribute value
        if (i < tagString.length) {
          const quoteChar = tagString[i];

          if (quoteChar === '"' || quoteChar === "'") {
            const endQuoteIndex = tagString.indexOf(quoteChar, i + 1);

            if (endQuoteIndex !== -1) {
              const attrValue = tagString.substring(i, endQuoteIndex + 1);
              const innerValue = attrValue.substring(1, attrValue.length - 1);

              // Special handling for class attribute values
              if (attrName === 'class') {
                // Add opening quote
                tokens.push({ type: 'string', value: quoteChar });

                // Parse individual class names
                const classNames = innerValue.trim().split(/\s+/);
                for (let j = 0; j < classNames.length; j++) {
                  if (classNames[j]) {
                    tokens.push({ type: 'class-name', value: classNames[j] });
                    // Add space between classes (except last one)
                    if (j < classNames.length - 1 && innerValue.includes(' ')) {
                      tokens.push({ type: 'text', value: ' ' });
                    }
                  }
                }

                // Add closing quote
                tokens.push({ type: 'string', value: quoteChar });
              }
              // Special handling for id attribute values
              else if (attrName === 'id') {
                // Add opening quote
                tokens.push({ type: 'string', value: quoteChar });
                // ID value
                tokens.push({ type: 'id-value', value: innerValue });
                // Add closing quote
                tokens.push({ type: 'string', value: quoteChar });
              }
              // Check if the value is a color
              else if (isValidColor(innerValue)) {
                const parsed = parseColor(innerValue);
                tokens.push({
                  type: 'color',
                  value: attrValue,
                  colorPreview: parsed ? toRgbaString(parsed) : undefined
                });
              }
              // Regular string value
              else {
                tokens.push({ type: 'string', value: attrValue });
              }

              i = endQuoteIndex + 1;
            } else {
              // No closing quote found
              tokens.push({ type: 'string', value: tagString.substring(i) });
              i = tagString.length;
            }
          } else {
            // Unquoted attribute value
            const unquotedMatch = tagString.substring(i).match(/^([^\s>]+)/);
            if (unquotedMatch) {
              tokens.push({ type: 'string', value: unquotedMatch[0] });
              i += unquotedMatch[0].length;
            }
          }
        }
      }
    } else {
      // Invalid attribute, skip character
      tokens.push({ type: 'text', value: tagString[i] });
      i++;
    }
  }

  // Closing part of tag (/ and >)
  const closingPart = tagString.substring(i);
  if (closingPart) {
    tokens.push({ type: 'tag', value: closingPart });
  }
}

/**
 * Convert tokens to HTML with syntax highlighting classes
 */
export function tokensToHtml(tokens: HighlightToken[]): string {
  return tokens
    .map((token) => {
      const escapedValue = escapeHtml(token.value);

      // Add color swatch for color values
      if (token.type === 'color' && token.colorPreview) {
        return `<span class="color-value-wrapper"><span class="color-swatch" style="background: ${token.colorPreview}"></span><span class="syntax-${token.type}">${escapedValue}</span></span>`;
      }

      return `<span class="syntax-${token.type}">${escapedValue}</span>`;
    })
    .join('');
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Apply syntax highlighting to HTML code
 */
export function highlightHtml(html: string): string {
  const tokens = tokenizeHtml(html);
  return tokensToHtml(tokens);
}
